// Vocabulario propio: las palabras de cada seminario salen de nuestra copia
// (public/vocabulario/seminario-NN.json, generada con catalogo-vocabulario/armar_catalogo.py)
// en vez de la API de Inowu. El progreso del alumno (las 7 bolitas) sigue viniendo de Inowu
// y se le pega a cada palabra; si Inowu no responde, las palabras se muestran sin progreso.
// Con el avance propio encendido (AvancePropio.ts) el progreso sale de nuestro Firestore y ya no se
// le pregunta nada a Inowu.
//
// Interruptor:
//   · REACT_APP_VOCABULARIO=propio  lo enciende para todos (por omisión está apagado);
//   · localStorage "vocabulario" = "propio" o "inowu"  lo cambia solo en ese navegador, para probar.

import { auth } from "initFirebase";
import { avanceDe, avanceDeAlumno, camposComoInowu, IDIOMAS, usarAvancePropio } from "./AvancePropio";

interface SeminarioPropio {
	seminario: number;
	palabras: Array<Record<string, any>>;
	categorias: Array<string>;
	subcategorias: Array<string>;
}

const CAMPOS_DE_PROGRESO = [
	"dominada",
	"veces_respondida",
	"veces_respondida_correcta",
	"dificultadP",
	"firstStudied",
	"lastStudied",
];
const SIN_PROGRESO = {
	dominada: null,
	veces_respondida: null,
	veces_respondida_correcta: null,
	dificultadP: "0",
	firstStudied: null,
	lastStudied: null,
};
// El seminario «cero» (61) se copió del respaldo del 4 nov 2026 (scripts/vocabulario/armar_cero_e_indice.py).
const ULTIMO_PROPIO = 61;

const cache = new Map<number, Promise<SeminarioPropio>>();

export function usarVocabularioPropio(): boolean {
	if (usarAvancePropio()) return true;
	try {
		const local = window.localStorage.getItem("vocabulario");
		if (local === "propio") return true;
		if (local === "inowu") return false;
	} catch {
		// Sin acceso al almacenamiento: manda la configuración del sitio.
	}
	return process.env.REACT_APP_VOCABULARIO === "propio";
}

export function tieneSeminarioPropio(seminario: number): boolean {
	return usarVocabularioPropio() && seminario >= 1 && seminario <= ULTIMO_PROPIO;
}

export function seminarioPropio(seminario: number): Promise<SeminarioPropio> {
	if (!cache.has(seminario)) {
		const archivo = `${process.env.PUBLIC_URL}/vocabulario/seminario-${String(seminario).padStart(2, "0")}.json`;
		const pedido = fetch(archivo).then((res) => {
			if (!res.ok) throw new Error(`No se pudo leer ${archivo}: ${res.status}`);
			return res.json();
		});
		pedido.catch(() => cache.delete(seminario));
		cache.set(seminario, pedido);
	}
	return cache.get(seminario)!;
}

/** Misma forma que `seminary/{alumno}/{n}` de Inowu: { seminaries: [...] }. */
export async function seminarioConProgreso(
	seminario: number,
	progresoDeInowu: () => Promise<any>
): Promise<{ seminaries: Array<Record<string, any>> }> {
	if (usarAvancePropio()) {
		throw new Error("Con el avance propio se usa seminarioConAvancePropio");
	}
	const [propio, inowu] = await Promise.all([
		seminarioPropio(seminario),
		progresoDeInowu().catch((error) => {
			console.warn("Inowu no respondió; el vocabulario se muestra sin progreso.", error);
			return null;
		}),
	]);
	const progreso = new Map<number, Record<string, any>>();
	(inowu?.seminaries ?? []).forEach((palabra) => progreso.set(palabra.id, palabra));
	return {
		seminaries: propio.palabras.map((palabra) => {
			const suyo = progreso.get(palabra.id);
			const campos = suyo
				? Object.fromEntries(CAMPOS_DE_PROGRESO.map((campo) => [campo, suyo[campo]]))
				: SIN_PROGRESO;
			return { ...palabra, ...campos };
		}),
	};
}

/** Misma forma que `categories/{n}` de Inowu. */
export async function categoriasPropias(seminario: number) {
	const { categorias } = await seminarioPropio(seminario);
	return { categories: categorias.map((categoria, i) => ({ id: i + 1, categoria })) };
}

/** Misma forma que `subcategories/{n}` de Inowu. */
export async function subcategoriasPropias(seminario: number) {
	const { subcategorias } = await seminarioPropio(seminario);
	return { subCategories: subcategorias.map((sub_categoria, i) => ({ id: i + 1, sub_categoria })) };
}

// ---------------------------------------------------------------------------------------------
// Con el avance propio: palabras de la copia + progreso de nuestro Firestore.
// ---------------------------------------------------------------------------------------------

/** Misma forma que `seminary/{alumno}/{n}` de Inowu, con el avance propio. */
export async function seminarioConAvancePropio(vocabularyUserId: number | string, seminario: number) {
	const [propio, avance] = await Promise.all([seminarioPropio(seminario), avanceDeAlumno(vocabularyUserId)]);
	return {
		seminaries: propio.palabras.map((palabra) => ({ ...palabra, ...camposComoInowu(avance.get(Number(palabra.id))) })),
	};
}

async function miAvance() {
	const uid = auth.currentUser?.uid;
	if (!uid) throw new Error("Sin sesión: no hay avance que mostrar");
	return avanceDe(uid);
}

async function palabrasDelRango(inicio: number, fin: number) {
	const numeros: number[] = [];
	for (let n = Number(inicio); n <= Number(fin); n++) if (n >= 1 && n <= ULTIMO_PROPIO) numeros.push(n);
	const seminarios = await Promise.all(numeros.map(seminarioPropio));
	const porId = new Map<number, Record<string, any>>();
	seminarios.forEach((s) => s.palabras.forEach((p) => porId.has(p.id) || porId.set(p.id, p)));
	return Array.from(porId.values());
}

/** Igual que `vocabulary/progress?startSeminary&endSeminary`: { totalWords, dominatedWords } (palabras distintas). */
export async function progresoDelRangoPropio(inicio: number, fin: number) {
	const [palabras, avance] = await Promise.all([palabrasDelRango(inicio, fin), miAvance()]);
	return {
		totalWords: palabras.length,
		dominatedWords: palabras.filter((p) => avance.get(Number(p.id))?.d === 3).length,
	};
}

/** Igual que `vocabulary/progress-languages`: [{ language, total, dominatedWords }] por idioma. */
export async function progresoPorIdiomaPropio(inicio: number, fin: number) {
	const [palabras, avance] = await Promise.all([palabrasDelRango(inicio, fin), miAvance()]);
	return IDIOMAS.map((language) => {
		const delIdioma = palabras.filter((p) => p.idioma === language);
		return {
			language,
			total: delIdioma.length,
			dominatedWords: delIdioma.filter((p) => avance.get(Number(p.id))?.d === 3).length,
		};
	}).filter((l) => l.total > 0);
}

/** La palabra con la forma que usa el sitio (la misma que arma VocabularySeminaryContext). */
export function comoPalabraDelSitio(word: Record<string, any>) {
	return {
		id: word.id,
		word_data_id: word.id,
		category: word.categoria ? String(word.categoria).split("|") : [],
		singular_category: word.singular_category,
		sub_category: word.sub_categorias ? String(word.sub_categorias).split("|") : [null],
		dominated: word.dominada,
		language: word.idioma,
		native_word: word.palabra,
		optional_word: word.palabra_opcional,
		meaning: word.significados ? String(word.significados).split("|") : [],
		word_id: word.word_id,
		warning: word.advertencia,
		times_answered: word.veces_respondida === null ? 0 : parseFloat(word.veces_respondida),
		times_correct_answer: word.veces_respondida_correcta === null ? 0 : parseFloat(word.veces_respondida_correcta),
		custom_difficulty: parseFloat(word.dificultadP),
		firstStudied: word.firstStudied,
		lastStudied: word.lastStudied,
		seminarios: word.seminarios,
		memory: word.memoria,
		grammar: word.gramatica,
		advice: word.consejo,
		fake_friend: word.falso_amigo,
		similarity: word.similitud,
		pronunciation: word.pronunciacion,
		did_YouKnow: word.sabias_que,
	};
}

/** Igual que `vocabulary/language-words`: las palabras de un idioma en el rango, con su avance. */
export async function palabrasPorIdiomaPropio(idioma: string, inicio: number, fin: number) {
	const [palabras, avance] = await Promise.all([palabrasDelRango(inicio, fin), miAvance()]);
	return palabras
		.filter((p) => p.idioma === idioma)
		.map((p) => comoPalabraDelSitio({ ...p, ...camposComoInowu(avance.get(Number(p.id))) }));
}

let indice: Promise<Record<string, number>> | null = null;
function indicePropio() {
	if (!indice) {
		indice = fetch(`${process.env.PUBLIC_URL}/vocabulario/indice.json`).then((r) => {
			if (!r.ok) throw new Error("No se pudo leer el índice del vocabulario");
			return r.json();
		});
		indice.catch(() => (indice = null));
	}
	return indice;
}

/** Los datos de una palabra sin avance (antes `words/word_info/{id}`, que devolvía { word: [ … ] }). */
export async function palabraPropia(id: number) {
	const seminario = (await indicePropio())[String(id)];
	if (!seminario) return null;
	const { palabras } = await seminarioPropio(seminario);
	return palabras.find((p) => Number(p.id) === Number(id)) || null;
}

/** Igual que `vocabulary/word-progress/{id}`: la palabra con su avance, con la forma del sitio. */
export async function progresoDePalabraPropio(id: number) {
	const [palabra, avance] = await Promise.all([palabraPropia(id), miAvance()]);
	if (!palabra) return null;
	return comoPalabraDelSitio({ ...palabra, ...camposComoInowu(avance.get(Number(id))) });
}
